import { useCallback, useEffect, useMemo } from 'react';
import type { GetAssignedTeamMembersDataQuery } from 'src/services/graphql/generated/graphql';
import { trackEvent } from 'src/analytics/events/trackEvent';
import { getHomeLoansDashboardImpressionEvent } from 'src/analytics/events/dashboard/getHomeLoansDashboardImpressionEvent';
import { getHomeLoansDashboardInteractionEvent } from 'src/analytics/events/dashboard/getHomeLoansDashboardInteractionEvent';
import {
    HOME_LOANS_DASHBOARD_IMPRESSION_EVENTS,
    HOME_LOANS_DASHBOARD_INTERACTION_EVENTS,
} from 'src/analytics/events/registeredEvents';
import { NO_LEAD_AVAILABLE, NO_STATUS_AVAILABLE } from 'src/analytics/constants';
import { SCHEDULER_CX_HELP_HOTLINE } from 'src/experiences/CallbackScheduler/constants';
import { TeamInformationCardVariant } from './types';
import type { TeamMember, AssignedTeamMembersProps } from './types';

type UseAssignedTeamMembersStateProps = {
    data: GetAssignedTeamMembersDataQuery | null;
};

export const useAssignedTeamMembersState = ({ data }: UseAssignedTeamMembersStateProps): AssignedTeamMembersProps => {
    const personalizedNextStepStage = useMemo(
        () => data?.homeLoansDashboardByLoanId?.personalizedNextSteps?.currentState ?? null,
        [data]
    );

    const leadId = useMemo(() => data?.zhlLoan?.lead?.id ?? NO_LEAD_AVAILABLE, [data]);

    const teamMembers: TeamMember[] = useMemo(() => {
        const { loanOfficer, loanProcessor } = data?.zhlLoan ?? {};
        const members = [loanOfficer, loanProcessor];
        return members.filter((member): member is TeamMember => !!member && (!!member.officePhone || !!member.email));
    }, [data]);

    const variant = useMemo(
        () => (teamMembers.length > 0 ? TeamInformationCardVariant.TEAM_ASSIGNED : TeamInformationCardVariant.NO_TEAM),
        [teamMembers.length]
    );

    const journeyStage = personalizedNextStepStage ?? NO_STATUS_AVAILABLE;

    useEffect(() => {
        if (!data) return;
        trackEvent(
            getHomeLoansDashboardImpressionEvent({
                event: HOME_LOANS_DASHBOARD_IMPRESSION_EVENTS.TEAM_INFORMATION_CARD_MODULE_LOADED,
                lead_id: leadId,
                journey_stage_cd: journeyStage,
            })
        );
    }, [data, leadId, journeyStage]);

    const onCall = useCallback(
        (phoneNumber: string, isCallOrText = false) => {
            trackEvent(
                getHomeLoansDashboardInteractionEvent({
                    event: isCallOrText
                        ? HOME_LOANS_DASHBOARD_INTERACTION_EVENTS.TEAM_INFORMATION_CARD_CALL_OR_TEXT_CTA_CLICK
                        : HOME_LOANS_DASHBOARD_INTERACTION_EVENTS.TEAM_INFORMATION_CARD_CALL_CTA_CLICK,
                    lead_id: leadId,
                    journey_stage_cd: journeyStage,
                })
            );
        },
        [leadId, journeyStage]
    );

    const onEmail = useCallback(
        (_email: string) => {
            trackEvent(
                getHomeLoansDashboardInteractionEvent({
                    event: HOME_LOANS_DASHBOARD_INTERACTION_EVENTS.TEAM_INFORMATION_CARD_EMAIL_CTA_CLICK,
                    lead_id: leadId,
                    journey_stage_cd: journeyStage,
                })
            );
        },
        [leadId, journeyStage]
    );

    return {
        variant,
        teamMembers,
        personalizedNextStepStage,
        helpHotline: SCHEDULER_CX_HELP_HOTLINE,
        onCall,
        onEmail,
    };
};
